'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { HugeiconsIcon } from '@hugeicons/react'
import { MenuTwoLineIcon, SearchIcon, BubbleChatSpark01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { UserIcon } from '@hugeicons-pro/core-stroke-rounded'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'
import { AppSidebarUserMenu } from '@/components/AppSidebarUserMenu'
import { SidebarShortcutTooltip } from '@/components/SidebarShortcutTooltip'
import { useLobbyHeaderMeta, useLobbyHeaderTitle } from '@/contexts/LobbyHeaderMetaContext'
import { resolveLobbyPageTitle } from '@/lib/lobby/resolve-lobby-page-title'
import { useCachedUser } from '@/hooks/useCachedUser'
import { useChat } from '@/contexts/ChatContext'
import { createClient } from '@/utils/supabase/client'
import type { Tables } from '@/utils/schema'
import { cn } from '@/lib/utils'

const headerIconButtonClass =
  'group relative flex size-9 shrink-0 cursor-default items-center justify-center rounded-lg border-0 bg-transparent p-0 text-atrium-black transition-[background-color,color] duration-200 ease-out motion-reduce:transition-none hover:bg-atrium-content active:bg-atrium-content active:text-accent-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2 focus-visible:ring-offset-atrium-background'

type LobbyHeaderProps = {
  onOpenCommandSearch: () => void
  onAccountOpenChange: (open: boolean) => void
  avatarRefreshKey?: number
  hideAppSidebar?: boolean
}

export function LobbyHeader({
  onOpenCommandSearch,
  onAccountOpenChange,
  avatarRefreshKey = 0,
  hideAppSidebar = false,
}: LobbyHeaderProps) {
  const pathname = usePathname() ?? ''
  const pageTitle = resolveLobbyPageTitle(pathname)
  const headerTitle = useLobbyHeaderTitle()
  const headerMeta = useLobbyHeaderMeta()
  const { user, isLoading: userLoading } = useCachedUser()
  const {
    toggleAppSidebar,
    appSidebarRailMode,
    appSidebarOverlayOpen,
    setAppSidebarOverlayOpen,
    panelViewportFullscreen,
    toggleChat,
    chatOpen,
  } = useChat()
  const supabase = useMemo(() => createClient(), [])
  const [publicProfile, setPublicProfile] = useState<Tables<'public_profiles'> | null>(null)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)

  const sidebarExpanded = appSidebarRailMode === 'expanded'

  useEffect(() => {
    let cancelled = false

    const fetchProfile = async () => {
      if (!user) {
        setPublicProfile(null)
        setAvatarUrl(null)
        setProfileLoading(false)
        return
      }

      setProfileLoading(true)
      try {
        const { data: profile, error } = await supabase
          .from('public_profiles')
          .select('*')
          .eq('auth_user_id', user.id)
          .single()

        if (cancelled) return

        if (error) {
          setPublicProfile(null)
          setAvatarUrl(null)
          return
        }

        const nextProfile = profile as Tables<'public_profiles'>
        setPublicProfile(nextProfile)

        if (nextProfile.avatar_path) {
          const { data } = supabase.storage.from('avatars').getPublicUrl(nextProfile.avatar_path)
          setAvatarUrl(data.publicUrl)
        } else {
          setAvatarUrl(null)
        }
      } catch {
        if (!cancelled) {
          setPublicProfile(null)
          setAvatarUrl(null)
        }
      } finally {
        if (!cancelled) setProfileLoading(false)
      }
    }

    fetchProfile()
    return () => {
      cancelled = true
    }
  }, [user, avatarRefreshKey, supabase])

  const handleSidebarToggle = useCallback(() => {
    if (hideAppSidebar || panelViewportFullscreen) {
      setAppSidebarOverlayOpen(!appSidebarOverlayOpen)
      return
    }
    toggleAppSidebar()
  }, [hideAppSidebar, panelViewportFullscreen, appSidebarOverlayOpen, setAppSidebarOverlayOpen, toggleAppSidebar])

  const showAdminNav = !!user?.email?.endsWith('@atrium.run')
  const sidebarToggleExpanded =
    hideAppSidebar || panelViewportFullscreen ? appSidebarOverlayOpen : sidebarExpanded
  const sidebarToggleLabel = sidebarToggleExpanded ? 'Collapse sidebar' : 'Expand sidebar'

  return (
    <TooltipProvider delayDuration={0} skipDelayDuration={0}>
      <header
        data-lobby-header
        className="hidden h-12 w-full shrink-0 items-center gap-3 border-b border-atrium-border/50 bg-atrium-background px-4 lg:flex"
      >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={sidebarToggleLabel}
              className={cn(
                headerIconButtonClass,
                sidebarToggleExpanded ? 'cursor-w-resize' : 'cursor-e-resize'
              )}
              onClick={handleSidebarToggle}
            >
              <HugeiconsIcon icon={MenuTwoLineIcon} className="size-[18px] shrink-0 opacity-70" />
            </button>
          </TooltipTrigger>
            <TooltipContent side="bottom">
              <SidebarShortcutTooltip label={sidebarToggleLabel} shortcutKey="J" />
            </TooltipContent>
          </Tooltip>
        <div className="flex min-w-0 items-center gap-3.5">
          {headerTitle ? (
            <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 truncate text-[15px] text-atrium-black">
              {headerTitle}
            </nav>
          ) : (
            <>
              <h1 className="truncate text-[15px] font-semibold text-atrium-black">{pageTitle}</h1>
              {headerMeta ? (
                <span className="truncate font-mono text-xs text-atrium-black/45">{headerMeta}</span>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Search"
              className={headerIconButtonClass}
              onClick={onOpenCommandSearch}
            >
              <HugeiconsIcon icon={SearchIcon} className="size-[18px] shrink-0 opacity-70" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <SidebarShortcutTooltip label="Search" shortcutKey="K" />
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label={chatOpen ? 'Close chat' : 'Open chat'}
              className={headerIconButtonClass}
              onClick={toggleChat}
            >
              <span className="flex size-7 items-center justify-center rounded-full bg-accent-700">
                <HugeiconsIcon icon={BubbleChatSpark01Icon} className="size-[15px] text-white" />
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            {chatOpen ? 'Close chat' : 'Open chat'}
          </TooltipContent>
        </Tooltip>

        {userLoading ? (
          <Skeleton className="size-8 rounded-full bg-atrium-border/60" />
        ) : user ? (
          <AppSidebarUserMenu
            expanded
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
            user={user}
            publicProfile={publicProfile}
            avatarUrl={avatarUrl}
            profileLoading={profileLoading}
            setAccountOpen={onAccountOpenChange}
            showAdminNav={showAdminNav}
            tooltipSide="bottom"
          />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Open account options"
                className={cn(
                  headerIconButtonClass,
                  'size-8 rounded-full bg-atrium-content text-atrium-muted hover:text-atrium-black'
                )}
                onClick={() => onAccountOpenChange(true)}
              >
                <HugeiconsIcon icon={UserIcon} className="size-[18px] opacity-70" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Account</TooltipContent>
          </Tooltip>
        )}
      </div>
      </header>
    </TooltipProvider>
  )
}
